import { Link } from 'react-router-dom'
import { Icon } from './icons.jsx'

// Shared layout primitives + the two components every page ends on.

export function Container({ className = '', children }) {
  return <div className={`mx-auto w-full max-w-site px-5 sm:px-6 ${className}`}>{children}</div>
}

// tone: 'white' (default) | 'surface' (light grey) | 'dark' (near-black band)
export function Section({ id, tone = 'white', className = '', children }) {
  const tones = {
    white: 'bg-white',
    surface: 'bg-surface',
    dark: 'bg-night text-white',
  }
  return (
    <section id={id} className={`py-16 sm:py-[88px] ${tones[tone]} ${className}`}>
      <Container>{children}</Container>
    </section>
  )
}

export function Eyebrow({ children, tone = 'primary' }) {
  const color = tone === 'white' ? 'text-[#ffb27a]' : 'text-primary-dark'
  return (
    <span
      className={`inline-flex items-center gap-x-2 text-[12px] font-bold uppercase tracking-[0.14em] ${color}`}
    >
      <span className="h-[6px] w-[6px] rounded-full bg-primary" />
      {children}
    </span>
  )
}

export function SectionHead({ eyebrow, title, body, align = 'center', tone = 'ink' }) {
  const isCenter = align === 'center'
  const titleColor = tone === 'white' ? 'text-white' : 'text-ink'
  const bodyColor = tone === 'white' ? 'text-[#b6bfd0]' : 'text-dim'
  return (
    <div
      className={`flex flex-col gap-y-4 ${isCenter ? 'mx-auto max-w-[720px] items-center text-center' : 'max-w-[720px] items-start text-left'}`}
    >
      {eyebrow ? <Eyebrow tone={tone === 'white' ? 'white' : 'primary'}>{eyebrow}</Eyebrow> : null}
      <h2
        className={`text-[30px] font-bold leading-[1.1] tracking-[-0.022em] sm:text-[40px] ${titleColor}`}
      >
        {title}
      </h2>
      {body ? <p className={`text-[17px] leading-[1.6] ${bodyColor}`}>{body}</p> : null}
    </div>
  )
}

// variant: primary (orange) | dark (near-black) | ghost (outlined) | white
// size: md | sm | lg
export function Button({
  to,
  href,
  type = 'button',
  variant = 'primary',
  size = 'md',
  icon,
  className = '',
  children,
  ...rest
}) {
  const variants = {
    primary: 'bg-primary text-white hover:bg-primary-dark',
    dark: 'bg-night text-white hover:bg-night-2',
    ghost: 'border border-line bg-transparent text-ink hover:bg-surface',
    white: 'bg-white text-ink hover:bg-[#fff4ec]',
    outlineWhite: 'border border-[rgba(255,255,255,0.35)] bg-transparent text-white hover:bg-[rgba(255,255,255,0.08)]',
  }
  const sizes = {
    sm: 'px-[14px] py-[8px] text-[13px]',
    md: 'px-[18px] py-[11px] text-[15px]',
    lg: 'px-[24px] py-[14px] text-[16px]',
  }
  const cls = `inline-flex items-center justify-center gap-x-2 rounded-[9px] font-semibold whitespace-nowrap transition-colors ${variants[variant]} ${sizes[size]} ${className}`
  const inner = (
    <>
      {children}
      {icon ? <Icon name={icon} size={size === 'sm' ? 15 : 17} /> : null}
    </>
  )
  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {inner}
      </Link>
    )
  }
  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {inner}
      </a>
    )
  }
  return (
    <button type={type} className={cls} {...rest}>
      {inner}
    </button>
  )
}

export function IconTile({ name, tone = 'tint', className = '' }) {
  const tones = {
    tint: 'bg-primary-tint text-primary-dark',
    dark: 'bg-[rgba(255,255,255,0.08)] text-[#ffb27a]',
    white: 'bg-white text-primary-dark shadow-card',
  }
  return (
    <span
      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] ${tones[tone]} ${className}`}
    >
      <Icon name={name} size={22} />
    </span>
  )
}

export function CheckList({ items, tone = 'ink' }) {
  const text = tone === 'white' ? 'text-[#dbe1ec]' : 'text-ink'
  return (
    <ul className="flex flex-col gap-y-3">
      {items.map((item) => (
        <li key={item} className={`flex items-start gap-x-3 text-[15px] leading-[1.5] ${text}`}>
          <span className="mt-[2px] inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white">
            <Icon name="check" size={12} strokeWidth={3} />
          </span>
          {item}
        </li>
      ))}
    </ul>
  )
}

// The orange closing band every page ends on.
export function CtaBand({
  title = 'Ready for a calendar full of booked jobs?',
  body = 'Get a free 48-hour audit of your website, Google profile, ads and reviews. No pitch deck, just what is leaking and what it is worth.',
  phone,
}) {
  return (
    <section className="bg-primary py-16 sm:py-20">
      <Container>
        <div className="flex flex-col items-start gap-y-8 lg:flex-row lg:items-center lg:justify-between lg:gap-x-12">
          <div className="flex max-w-[640px] flex-col gap-y-3">
            <h2 className="text-[30px] font-bold leading-[1.1] tracking-[-0.022em] text-white sm:text-[40px]">
              {title}
            </h2>
            <p className="text-[17px] leading-[1.6] text-[rgba(255,255,255,0.86)]">{body}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button to="/contact" variant="dark" size="lg" icon="arrowRight">
              Get my free audit
            </Button>
            {phone ? (
              <Button href={phone.href} variant="outlineWhite" size="lg" icon="phone">
                Call {phone.label}
              </Button>
            ) : null}
          </div>
        </div>
      </Container>
    </section>
  )
}

// Inner-page hero: light band, left-aligned.
export function PageHero({ eyebrow, title, body, children }) {
  return (
    <section className="border-b border-line bg-surface">
      <Container>
        <div className="flex max-w-[760px] flex-col items-start gap-y-5 py-14 sm:py-20">
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          <h1 className="text-[36px] font-extrabold leading-[1.06] tracking-[-0.03em] text-ink sm:text-[52px]">
            {title}
          </h1>
          {body ? <p className="text-[18px] leading-[1.6] text-dim">{body}</p> : null}
          {children}
        </div>
      </Container>
    </section>
  )
}
